import React, { useState, useRef, useEffect, useCallback } from "react";
import api from "../../services/api";
import { PageContainer, PageHeader, Card } from "../ui/index";
import { useAuth } from "../../context/AuthContext";

interface KarenAction {
  tool: string;
  summary: string;
  ok: boolean;
}

interface Message {
  id: number;
  role: "user" | "karen";
  text: string;
  sources?: string[];
  actions?: KarenAction[];
  timestamp: Date;
}

// Owner/manager: Karen can act (drafts, counts, prices, timecards) - every
// change is previewed and confirmed first, and drafts wait for Paula.
const MANAGER_QUESTIONS = [
  "What's low from Kirchner? Draft an order.",
  "What's low from Origlio?",
  "Receive today's Wilsbach delivery",
  "Bud Light 12/16 cans went up to 21.60 a case - what should the 12-pack sell for?",
  "Count: 7 cases of Troegs Perpetual 6-packs in the back cooler",
  "Mark Mango Rita as out at the wholesaler",
  "Fix Sam's clock-out yesterday, he left at 9:15",
  "How did we do this week?",
];

const SUGGESTED_QUESTIONS = [
  "How do I process a return?",
  "How does dual pricing work?",
  "What's running low on stock?",
  "How do loyalty points work?",
  "Who are our main distributors?",
  "What's the closing checklist?",
  "How do I sell a gift card?",
  "Do we carry Troegs?",
];

export default function AskKarenPage() {
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "manager";
  const suggestions = isManager ? MANAGER_QUESTIONS : SUGGESTED_QUESTIONS;
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 0,
      role: "karen",
      text: "Hi! I'm Karen, your RBW assistant. I can help with product info, store procedures, inventory questions, and more. What can I help you with?",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || loading) return;

    const userMsg: Message = {
      id: Date.now(),
      role: "user",
      text: text.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      // Build conversation history from previous messages (exclude the greeting)
      const prevMessages = messages.filter(m => m.id !== 0);
      const conversation_history = prevMessages.map(m => ({
        role: m.role === "karen" ? "assistant" : "user",
        content: m.text,
      }));

      const res = await api.post("/ai/ask-karen", {
        question: text.trim(),
        conversation_history,
      });
      const karenMsg: Message = {
        id: Date.now() + 1,
        role: "karen",
        text: res.data.answer,
        sources: res.data.sources,
        actions: Array.isArray(res.data.actions) ? res.data.actions : [],
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, karenMsg]);
    } catch {
      setMessages(prev => [
        ...prev,
        {
          id: Date.now() + 1,
          role: "karen",
          text: "Sorry, I ran into an issue. Try again, or ask Patrick or Paula for help.",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [loading, messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Ask Karen"
        subtitle="Your AI assistant for store operations, product info, and procedures"
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Chat area */}
        <div className="lg:col-span-3">
          <Card>
            {/* Messages */}
            <div
              className="p-4 space-y-4 overflow-y-auto"
              style={{ minHeight: "400px", maxHeight: "calc(100vh - 320px)" }}
            >
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.role === "user" ? "rounded-br-md" : "rounded-bl-md"
                    }`}
                    style={{
                      background: msg.role === "user" ? "var(--rbw-navy)" : "#f3f4f6",
                      color: msg.role === "user" ? "white" : "var(--rbw-navy)",
                    }}
                  >
                    {msg.role === "karen" && (
                      <div className="flex items-center gap-2 mb-1.5">
                        <div
                          className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                          style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
                        >
                          K
                        </div>
                        <span className="text-xs font-semibold" style={{ color: "var(--rbw-gold)" }}>
                          Karen
                        </span>
                      </div>
                    )}
                    <div className="text-sm whitespace-pre-wrap leading-relaxed">
                      {msg.text.split("\n").map((line, i) => {
                        // Bold markdown-style headers
                        if (line.startsWith("**") && line.endsWith("**")) {
                          return (
                            <p key={i} className="font-bold mt-2 mb-1">
                              {line.replace(/\*\*/g, "")}
                            </p>
                          );
                        }
                        // Bold inline
                        if (line.includes("**")) {
                          const parts = line.split(/\*\*/);
                          return (
                            <p key={i}>
                              {parts.map((part, j) =>
                                j % 2 === 1 ? (
                                  <strong key={j}>{part}</strong>
                                ) : (
                                  <span key={j}>{part}</span>
                                )
                              )}
                            </p>
                          );
                        }
                        if (line.startsWith("- ")) {
                          return (
                            <p key={i} className="pl-3">
                              {line}
                            </p>
                          );
                        }
                        if (line.trim() === "") return <br key={i} />;
                        return <p key={i}>{line}</p>;
                      })}
                    </div>
                    {msg.actions && msg.actions.length > 0 && (
                      <div className="mt-2 pt-2 border-t space-y-1" style={{ borderColor: "#e5e7eb" }}>
                        {msg.actions.map((a, i) => (
                          <div
                            key={i}
                            className="text-xs px-2 py-1 rounded-md font-medium"
                            style={{ background: a.ok ? "#ecfdf5" : "#fef2f2", color: a.ok ? "#065f46" : "#991b1b" }}
                          >
                            {a.ok ? "Done: " : "Failed: "}{a.summary}
                          </div>
                        ))}
                      </div>
                    )}
                    {msg.sources && msg.sources.length > 0 && (
                      <div className="mt-2 pt-2 border-t flex flex-wrap gap-1" style={{ borderColor: "#e5e7eb" }}>
                        {msg.sources.map((s, i) => (
                          <span
                            key={i}
                            className="text-xs px-2 py-0.5 rounded-full"
                            style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)", opacity: 0.8 }}
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="rounded-2xl rounded-bl-md px-4 py-3" style={{ background: "#f3f4f6" }}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                        style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
                      >
                        K
                      </div>
                      <div className="flex gap-1">
                        <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: "var(--rbw-navy)", animationDelay: "0ms" }} />
                        <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: "var(--rbw-navy)", animationDelay: "150ms" }} />
                        <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: "var(--rbw-navy)", animationDelay: "300ms" }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form
              onSubmit={handleSubmit}
              className="flex items-center gap-3 p-4 border-t"
              style={{ borderColor: "var(--rbw-border)" }}
            >
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask Karen anything about the store..."
                disabled={loading}
                className="flex-1 px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 transition-all"
                style={{
                  borderColor: "var(--rbw-border)",
                  background: "white",
                }}
                autoFocus
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold transition-all"
                style={{
                  background: input.trim() && !loading ? "var(--rbw-gold)" : "#e5e7eb",
                  color: input.trim() && !loading ? "var(--rbw-navy)" : "#9ca3af",
                  cursor: input.trim() && !loading ? "pointer" : "not-allowed",
                }}
              >
                Send
              </button>
            </form>
          </Card>
        </div>

        {/* Sidebar - suggested questions */}
        <div className="lg:col-span-1">
          <Card>
            <div className="p-4">
              <h3 className="text-sm font-bold mb-3" style={{ color: "var(--rbw-navy)" }}>
                Quick Questions
              </h3>
              <div className="space-y-2">
                {suggestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(q)}
                    disabled={loading}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm transition-all hover:shadow-sm"
                    style={{
                      background: "#f9fafb",
                      color: "var(--rbw-navy)",
                      border: "1px solid var(--rbw-border)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--rbw-gold)";
                      e.currentTarget.style.borderColor = "var(--rbw-gold)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#f9fafb";
                      e.currentTarget.style.borderColor = "var(--rbw-border)";
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-4 mt-4">
              <h3 className="text-sm font-bold mb-2" style={{ color: "var(--rbw-navy)" }}>
                What Karen knows
              </h3>
              <ul className="text-xs space-y-1.5" style={{ color: "var(--rbw-gray-dark)" }}>
                <li>Product catalog & pricing</li>
                <li>Inventory levels & par status</li>
                <li>Vendor information</li>
                <li>Store procedures & policies</li>
                <li>Loyalty program details</li>
                <li>Shift checklists</li>
                <li>Returns & gift card process</li>
              </ul>
            </div>
          </Card>
        </div>
      </div>
    </PageContainer>
  );
}
